import { Container, Grid, NumberInput, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';

import { showNotification } from '@mantine/notifications';
import React, { ReactNode, useEffect } from 'react';
import { X } from 'tabler-icons-react';
import { UserManagementFilterMode } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { BasicMerchant } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  SelectData,
  UserManagementFilterData,
  UserManagementFilterForm,
} from '../../type/filter.types';
import {
  BasicBranch,
  BasicPartner,
  BasicPos,
  BasicReseller,
} from '../../type/select.types';
import { CurrenciesData } from '../../data/currencies-data.array';
import { MdrFee } from '../../type/onboard-merchant-request.types';

interface Props {
  filterValues: UseFormReturnType<UserManagementFilterForm>;
  setIsLoadPartners: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadPartners: boolean;
  filterSelectData: UseFormReturnType<UserManagementFilterData>;
  mode?: UserManagementFilterMode;
  mdrFees?: MdrFee[];
  setMdrFees: React.Dispatch<React.SetStateAction<MdrFee[] | undefined>>;
}

export const ResponsiveCol = ({
  children,
  mode,
}: {
  children: ReactNode;
  mode: UserManagementFilterMode | undefined;
}) => {
  return (
    <Grid.Col
      xs={12}
      sm={mode === UserManagementFilterMode.PAYMENT_LINK_MODE ? 3 : 6}
    >
      {children}
    </Grid.Col>
  );
};

export const MdrInputField = ({
  currency,
  mode,
  mdrFees,
  setMdrFees,
}: {
  currency: any;
  mode: UserManagementFilterMode | undefined;
  mdrFees?: MdrFee[];
  setMdrFees: React.Dispatch<React.SetStateAction<MdrFee[] | undefined>>;
}) => {
  const addMdrFee = (newMdr: any) => {
    if (mdrFees) {
      const index = mdrFees.findIndex(
        (mdrFee) => mdrFee.currencyId === newMdr.currencyId,
      );
      if (index === -1) return [...mdrFees, newMdr];
      return mdrFees.map((mdrFee) =>
        mdrFee.currencyId === newMdr.currencyId
          ? { ...mdrFee, ...newMdr }
          : mdrFee,
      );
    }
    return [newMdr];
  };

  const handlePercentageRateInput = (val: any, currency: any) => {
    setMdrFees(
      addMdrFee({ currencyId: parseInt(currency?.value), percentageRate: val }),
    );
  };

  const handleFlatRateInput = (val: any, currency: any) => {
    setMdrFees(
      addMdrFee({ currencyId: parseInt(currency?.value), flatRate: val }),
    );
  };

  const getDefaultValues = () =>
    mdrFees?.find((mdrFee) => parseInt(currency?.value) === mdrFee.currencyId);
  const defaultValues = getDefaultValues();

  return (
    <ResponsiveCol mode={mode} key={currency.value}>
      <NumberInput
        hideControls
        precision={2}
        decimalSeparator="."
        label={`${currency.label} MDR (Percentage Rate)`}
        onChange={(val) => handlePercentageRateInput(val, currency)}
        defaultValue={defaultValues?.percentageRate}
      />
      <NumberInput
        hideControls
        precision={2}
        decimalSeparator="."
        label={`${currency.label} MDR (Flat Rate)`}
        placeholder="(Optional)"
        onChange={(val) => handleFlatRateInput(val, currency)}
        defaultValue={defaultValues?.flatRate}
      />
    </ResponsiveCol>
  );
};

export const findValueInData = (data: SelectData[], value: string): boolean => {
  for (let i = 0; i < data?.length; i++) {
    if (data[i].value === value) {
      return true;
    }
  }
  return false;
};

function AlternativeUserFilter(props: Readonly<Props>) {
  const {
    filterValues,
    setIsLoadPartners,
    isLoadPartners,
    filterSelectData,
    mode,
    mdrFees,
    setMdrFees,
  } = props;

  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const selectLoadingData = useForm({
    initialValues: {
      isPartnersLoading: false,
      isResellersLoading: false,
      isMerchantsLoading: false,
      isBranchesLoading: false,
      isPosLoading: false,
    },
  });

  const isShowPartnerFilter = auth?.role === Roles.ADMINISTRATOR;
  const disableFields = !filterValues.values.partner;
  const noData = 'No data';
  const loadingData = 'Loading...';

  const loadPartners = async () => {
    try {
      const response = await axiosPrivate.get('/web/select/partners');
      const partners = response.data?.items?.map((partner: BasicPartner) => {
        return {
          label: partner.partnerName,
          value: partner.partnerId.toString(),
        };
      });
      filterSelectData.setFieldValue('partnersData', partners);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-partners-data',
        message:
          error.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    }
  };

  useEffect(() => {
    if (isLoadPartners) {
      loadPartners();
    } else {
      setIsLoadPartners(true);
    }
  }, []);

  useDidMountEffect(() => {
    let ignore = false;
    const loadResellers = async () => {
      selectLoadingData.setFieldValue('isResellersLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/resellers', {
          params: {
            partnerId: filterValues.values.partner,
          },
        });
        const resellers: SelectData[] = response.data?.items?.map(
          (reseller: BasicReseller) => {
            return {
              label: reseller.resellerName,
              value: reseller.resellerId.toString(),
            };
          },
        );

        //@ref https://stackoverflow.com/a/69725148
        if (!ignore) {
          const found = findValueInData(
            resellers,
            filterValues.values.reseller,
          );

          if (!found) {
            filterValues.setFieldValue('reseller', '');
          }
          filterSelectData.setFieldValue('resellersData', resellers);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-resellers-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isResellersLoading', false);
      }
    };
    if (!disableFields && filterValues.values.partner !== noData) {
      loadResellers();
    } else {
      filterValues.setFieldValue('reseller', '');
    }

    return () => {
      ignore = true;
    };
  }, [filterValues.values.partner]);

  useDidMountEffect(() => {
    let ignore = false;

    const loadMerchants = async () => {
      selectLoadingData.setFieldValue('isMerchantsLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/merchants', {
          params: {
            partnerId: filterValues.values.partner,
            resellerId: filterValues.values.reseller,
          },
        });

        const merchants = response.data?.items?.map(
          (merchant: BasicMerchant) => {
            return {
              label: merchant.merchantName,
              value: merchant.merchantId.toString(),
            };
          },
        );

        if (!ignore) {
          const found = findValueInData(
            merchants,
            filterValues.values.merchant,
          );

          if (!found) {
            filterValues.setFieldValue('merchant', '');
          }
          filterSelectData.setFieldValue('merchantsData', merchants);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-merchants-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isMerchantsLoading', false);
      }
    };

    if (!disableFields && filterValues.values.reseller) {
      loadMerchants();
    } else {
      filterValues.setFieldValue('merchant', '');
      filterSelectData.setFieldValue('merchantsData', []);
    }

    return () => {
      ignore = true;
    };
  }, [filterValues.values.reseller]);

  useDidMountEffect(() => {
    let ignore = false;

    const loadMerchantBranches = async () => {
      selectLoadingData.setFieldValue('isBranchesLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/branches', {
          params: {
            partnerId: filterValues.values.partner,
            resellerId: filterValues.values.reseller,
            merchantId: filterValues.values.merchant,
          },
        });

        const merchantBranches = response.data?.items?.map(
          (merchant: BasicBranch) => {
            return {
              label: merchant.branchName,
              value: merchant.branchId.toString(),
            };
          },
        );

        if (!ignore) {
          const found = findValueInData(
            merchantBranches,
            filterValues.values.merchantBranch,
          );

          if (!found) {
            filterValues.setFieldValue('merchantBranch', '');
          }
          filterSelectData.setFieldValue(
            'merchantBranchesData',
            merchantBranches,
          );
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-branches-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isBranchesLoading', false);
      }
    };

    if (!disableFields && filterValues.values.merchant) {
      loadMerchantBranches();
    } else {
      filterValues.setFieldValue('merchantBranch', '');
      filterSelectData.setFieldValue('merchantBranchesData', []);
    }

    return () => {
      ignore = true;
    };
  }, [filterValues.values.merchant]);

  useDidMountEffect(() => {
    let ignore = false;

    const loadPos = async () => {
      selectLoadingData.setFieldValue('isPosLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/pos', {
          params: {
            partnerId: filterValues.values.partner,
            resellerId: filterValues.values.reseller,
            merchantId: filterValues.values.merchant,
            branchId: filterValues.values.merchantBranch,
          },
        });

        const pos = response.data?.items?.map((merchant: BasicPos) => {
          return {
            label: merchant.posName,
            value: merchant.posId.toString(),
          };
        });

        if (!ignore) {
          const found = findValueInData(pos, filterValues.values.pos ?? '');

          if (!found) {
            filterValues.setFieldValue('pos', '');
          }
          filterSelectData.setFieldValue('posData', pos);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-pos-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isPosLoading', false);
      }
    };

    if (!disableFields && filterValues.values.merchantBranch) {
      loadPos();
    } else {
      filterValues.setFieldValue('pos', '');
      filterSelectData.setFieldValue('posData', []);
    }

    return () => {
      ignore = true;
    };
  }, [filterValues.values.merchantBranch]);

  const isResellersLoading = selectLoadingData.values.isResellersLoading;
  const isResellersDataEmpty =
    filterSelectData.values.resellersData?.length === 0 && !disableFields;

  const getResellerData = () => {
    if (isResellersLoading) return [loadingData];
    if (isResellersDataEmpty) return [noData];
    return filterSelectData.values.resellersData;
  };

  const getResellerValue = () => {
    if (isResellersLoading) return loadingData;
    if (isResellersDataEmpty) return noData;
    return filterValues.values.reseller;
  };

  //Merchant
  const isMerchantsLoading = selectLoadingData.values.isMerchantsLoading;
  const isMerchantsDataEmpty =
    filterSelectData.values.merchantsData?.length === 0 &&
    filterValues.values.reseller;

  const getMerchantsData = () => {
    if (isMerchantsLoading) return [loadingData];
    if (isMerchantsDataEmpty) return [noData];
    return filterSelectData.values.merchantsData;
  };

  const getMerchantsValue = () => {
    if (isMerchantsLoading) return loadingData;
    if (isMerchantsDataEmpty) return noData;
    return filterValues.values.merchant;
  };

  //Branches

  const isBranchesLoading = selectLoadingData.values.isBranchesLoading;

  const isBranchesDataEmpty =
    filterSelectData.values.merchantBranchesData?.length === 0 &&
    filterValues.values.merchant;

  const getBranchesData = () => {
    if (isBranchesLoading) return [loadingData];
    if (isBranchesDataEmpty) return [noData];
    return filterSelectData.values.merchantBranchesData;
  };

  const getBranchesValue = () => {
    if (isBranchesLoading) return loadingData;
    if (isBranchesDataEmpty) return noData;
    return filterValues.values.merchantBranch;
  };

  const isSaveUnderBranch =
    filterValues.values.partner &&
    filterValues.values.reseller &&
    filterValues.values.merchant;

  const isPosLoading = selectLoadingData.values.isPosLoading;
  const isPosNoData =
    filterSelectData.values.posData?.length === 0 &&
    filterValues.values.merchantBranch;

  const getPosData = () => {
    if (selectLoadingData.values.isPosLoading) return [loadingData];

    if (!filterSelectData.values.posData) return [noData];
    if (
      filterSelectData.values.posData.length === 0 &&
      filterValues.values.merchantBranch
    )
      return [noData];
    return filterSelectData.values.posData;
  };

  const getPosValue = () => {
    if (isPosLoading) return loadingData;
    if (isPosNoData) return noData;
    return filterValues.values.pos;
  };

  return (
    <Container fluid p={0} m={0}>
      <Grid>
        <ResponsiveCol mode={mode}>
          {isShowPartnerFilter && (
            <Select
              label="Select Partner"
              data={filterSelectData.values.partnersData}
              onChange={(value) => {
                const partner = value ?? '';
                filterValues.setFieldValue('partner', partner);
              }}
              value={filterValues.values.partner}
              clearable
              searchable
            />
          )}
        </ResponsiveCol>
        <ResponsiveCol mode={mode}>
          <Select
            label="Select Reseller"
            data={getResellerData()}
            value={getResellerValue()}
            disabled={
              isResellersLoading ||
              disableFields ||
              isResellersDataEmpty ||
              filterValues.values.partner?.length === 0
            }
            onChange={(value: string) =>
              filterValues.setFieldValue('reseller', value)
            }
            clearable={
              !(
                selectLoadingData.values.isResellersLoading ||
                disableFields ||
                filterSelectData.values.resellersData?.length === 0 ||
                filterValues.values.partner?.length === 0
              )
            }
            searchable
          />
        </ResponsiveCol>
        <ResponsiveCol mode={mode}>
          <Select
            label="Select Merchant"
            data={getMerchantsData()}
            value={getMerchantsValue()}
            disabled={
              isMerchantsLoading ||
              filterSelectData.values.merchantsData?.length === 0 ||
              filterValues.values.reseller?.length === 0
            }
            onChange={(value: string) =>
              filterValues.setFieldValue('merchant', value)
            }
            clearable={
              !(
                selectLoadingData.values.isMerchantsLoading ||
                filterSelectData.values.merchantsData?.length === 0 ||
                filterValues.values.reseller?.length === 0
              )
            }
            searchable
          />
        </ResponsiveCol>
        {mode !== UserManagementFilterMode.APPROVE_ENTITY_MODE && (
          <ResponsiveCol mode={mode}>
            <Select
              label="Select Merchant Branch"
              data={getBranchesData()}
              value={getBranchesValue()}
              disabled={
                isBranchesLoading ||
                filterSelectData.values.merchantBranchesData?.length === 0 ||
                filterValues.values.merchant?.length === 0
              }
              onChange={(value: string) =>
                filterValues.setFieldValue('merchantBranch', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isBranchesLoading ||
                  filterSelectData.values.merchantBranchesData?.length === 0 ||
                  filterValues.values.merchant?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
        )}
        {mode === UserManagementFilterMode.PAYMENT_LINK_MODE && (
          <ResponsiveCol mode={mode}>
            <Select
              label="Select POS"
              data={getPosData()}
              value={getPosValue()}
              disabled={
                isPosLoading ||
                filterSelectData.values.posData?.length === 0 ||
                filterValues.values.merchantBranch?.length === 0
              }
              onChange={(value: string) =>
                filterValues.setFieldValue('pos', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isPosLoading ||
                  filterSelectData.values.posData?.length === 0 ||
                  filterValues.values.merchantBranch?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
        )}
      </Grid>
      {isSaveUnderBranch &&
        isShowPartnerFilter &&
        mode === UserManagementFilterMode.APPROVE_ENTITY_MODE && (
          <Grid>
            {CurrenciesData.map((currency) => {
              return (
                <MdrInputField
                  key={currency.value}
                  currency={currency}
                  mode={mode}
                  mdrFees={mdrFees}
                  setMdrFees={setMdrFees}
                />
              );
            })}
          </Grid>
        )}
    </Container>
  );
}

export default AlternativeUserFilter;
